import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";

const TYPES = [
  { id: "text", label: "Texto curto" },
  { id: "textarea", label: "Texto longo" },
  { id: "yes_no", label: "Sim / Não" },
  { id: "single_choice", label: "Escolha única" },
  { id: "multi_choice", label: "Múltipla escolha" },
];

export default function Anamnese() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/anamnesis/template").then(r => { setTemplate(r.data); setLoading(false); });
  }, []);

  if (loading || !template) return <div className="text-[#71717A]">Carregando…</div>;

  const setName = (v) => setTemplate({ ...template, name: v });
  const updQ = (i, k, v) => setTemplate(t => ({
    ...t, questions: t.questions.map((q, idx) => idx === i ? { ...q, [k]: v } : q)
  }));
  const addQ = () => setTemplate(t => ({
    ...t, questions: [...t.questions, { id: crypto.randomUUID(), label: "", type: "text", options: [], required: false }]
  }));
  const removeQ = (i) => setTemplate(t => ({ ...t, questions: t.questions.filter((_, idx) => idx !== i) }));
  const moveQ = (i, dir) => setTemplate(t => {
    const list = [...t.questions];
    const j = i + dir;
    if (j < 0 || j >= list.length) return t;
    [list[i], list[j]] = [list[j], list[i]];
    return { ...t, questions: list };
  });

  const save = async () => {
    try {
      const r = await api.put("/anamnesis/template", template);
      setTemplate(r.data);
      toast.success("Ficha salva!");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="anamnesis-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Ficha de Anamnese</h1>
        <p className="text-[#71717A] text-sm mt-1">Configure as perguntas que ficarão vinculadas à ficha de cada cliente.</p>
      </div>

      <div className="card-nc p-4 space-y-2">
        <label className="block text-xs font-semibold uppercase tracking-wide text-[#71717A]">Nome da ficha</label>
        <input data-testid="an-name" value={template.name} onChange={e => setName(e.target.value)} className="input-nc" />
      </div>

      <div className="space-y-2">
        {template.questions.map((q, i) => (
          <div key={q.id} data-testid={`an-q-${i}`} className="card-nc p-4 space-y-2">
            <div className="flex items-start gap-2">
              <div className="flex flex-col text-[#C98F96]">
                <button onClick={() => moveQ(i, -1)} className="text-xs">↑</button>
                <button onClick={() => moveQ(i, 1)} className="text-xs">↓</button>
              </div>
              <div className="flex-1 space-y-2">
                <input data-testid={`an-label-${i}`} placeholder="Pergunta" value={q.label}
                  onChange={e => updQ(i, "label", e.target.value)} className="input-nc font-semibold" />
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid={`an-type-${i}`} value={q.type} onChange={e => updQ(i, "type", e.target.value)} className="input-nc">
                    {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <label className="flex items-center gap-2 text-xs text-[#71717A] px-2">
                    <input data-testid={`an-req-${i}`} type="checkbox" checked={q.required} onChange={e => updQ(i, "required", e.target.checked)} />
                    Obrigatória
                  </label>
                </div>
                {(q.type === "single_choice" || q.type === "multi_choice") && (
                  <input data-testid={`an-opts-${i}`} placeholder="Opções separadas por vírgula"
                    value={(q.options || []).join(", ")}
                    onChange={e => updQ(i, "options", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                    className="input-nc" />
                )}
              </div>
              <button onClick={() => removeQ(i)} data-testid={`an-del-${i}`} className="text-[#B76E56] p-2"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <button onClick={addQ} data-testid="an-add" className="text-[#682F3E] font-semibold text-sm flex items-center gap-1">
        <Plus size={16} /> Adicionar pergunta
      </button>

      <div className="sticky bottom-20 lg:bottom-0 lg:relative pt-2">
        <button onClick={save} data-testid="an-save" className="btn-primary-nc w-full flex items-center justify-center gap-2 shadow-lg">
          <Save size={16} /> Salvar ficha
        </button>
      </div>
    </div>
  );
}
